import React, { useEffect, useCallback } from 'react';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Box, Button, Switch } from '@mui/material';
import CustomPagination from 'components/common/pagination/pagination';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSites, setParams, updateSiteSettingLocal } from 'store/sitesSlice';
import { showToast } from 'store/toastSlice';
import { formatDate } from 'utils/date';
import { siteService } from 'components/api/sites';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import styles from './sites.module.scss';

const Sites: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, total, isLoading, params } = useAppSelector((state) => state.sites);
  const navigate = useNavigate();

  const loadData = useCallback(() => {
    dispatch(fetchSites(params));
  }, [dispatch, params]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (id: string) => {
    navigate(`/sites/${id}`);
  };

  const handleToggleWithdrawManual = async (id: number, isWithdrawManual: boolean) => {
    try {
      dispatch(
        updateSiteSettingLocal({ id, settingKey: 'isWithdrawManual', value: isWithdrawManual }),
      );
      await siteService.updateSiteSettings(id, { isWithdrawManual });
      dispatch(showToast({ text: 'Settings updated successfully', type: 'success' }));
    } catch (err: any) {
      dispatch(
        updateSiteSettingLocal({ id, settingKey: 'isWithdrawManual', value: !isWithdrawManual }),
      );
      dispatch(
        showToast({
          text: err.response?.data?.message || err.message || 'Failed to update settings',
          type: 'error',
        }),
      );
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'title', headerName: 'Site Name', width: 200, sortable: true },
    { field: 'url', headerName: 'URL', width: 250, sortable: true },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 180,
      sortable: true,
      valueFormatter: (params: any) => formatDate(params),
    },
    {
      field: 'isWithdrawManual',
      headerName: 'Manual Withdraw',
      width: 150,
      renderCell: (params: any) => (
        <Switch
          checked={params.row.settings?.isWithdrawManual || false}
          onChange={(e) => {
            e.stopPropagation();
            handleToggleWithdrawManual(params.row.id, e.target.checked);
          }}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Button
            variant="contained"
            size="small"
            className={styles.editBtn}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(params.row.id);
            }}
          >
            Site Manage
          </Button>
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    dispatch(setParams({ page }));
  };

  const handlePageSizeChange = (limit: number) => {
    dispatch(setParams({ limit, page: 1 }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(
        setParams({
          sortField: model[0].field,
          sortValue: model[0].sort?.toUpperCase() as 'ASC' | 'DESC',
        }),
      );
    }
  };

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={styles.sitesContainer}>
      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data || []}
          rowCount={total || 0}
          getRowId={(row: any) => row.id}
          columns={columns}
          loading={isLoading}
          onSortModelChange={handleSortModelChange}
          sx={commonGridStyles}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default Sites;
